import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';

import { Metamodel } from 'app/model/base/model';
import { FileSystemCopySource } from 'app/model/pipeline/copysource/file-system.copysource';
import { format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import {
    commonFileTypeStoreGetMetadataActivityReadSettings,
    commonFileTypeStoreDeleteActivityReadSettings,
    commonFileTypeStoreWriteSettingControls,
    commonBinaryStoreWriteSettingControls,
    getCommonFileTypeStoreReadSettingControls,
    getCommonBinaryStoreReadSettingControls,
    getCommonDeltaStoreReadSettingControls,
    commonDeltaStoreWriteSettingControls,
    cdcDestination
} from '../consts/metadata-consts';
import { binaryMetadata } from '../dataset/binary-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { s3AuthType } from 'app/model/linkedservice/amazon-s3.linkedservice';
import {
    avro,
    cdm,
    delimited,
    delta,
    excel,
    json,
    orc,
    parquet,
    xml,
    iceberg
} from 'app/registries/dataflow/dataflow-file-format-metadata';
import { ConnectionAuthKind } from 'shell-models-lib';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';
import { deltaMetadata } from '../dataset/delta-metadata';

const accessKeyAkv: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    enableAKVParameterization: true,
    bind: 'accessKeyId',
    label: stringResources.ModelName_AccessKeyID,
    child: {
        type: 'parameterizable',
        attribute: 'accessKeyId',
        child: {
            type: 'textbox',
            label: stringResources.ModelName_AccessKeyID,
            required: true,
            isLabelVertical: true,
            validators: [{ type: 'required', errorMessage: '' }]
        }
    },
    description: format(
        stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AmazonS3_DisplayText),
        stringResources.ModelName_AccessKeyID
    )
};

const commonAccessProperties: ControlMetadata[] = [
    accessKeyAkv,
    {
        type: 'dynamic-akv-switcher',
        enableAKVParameterization: true,
        bind: 'secretAccessKey',
        label: stringResources.ModelName_AmazonAccessKey,
        child: {
            type: 'parameterizable',
            attribute: 'secretAccessKey',
            disableAddParameter: true,
            child: {
                type: 'password',
                label: stringResources.ModelName_AmazonAccessKey,
                required: true,
                isLabelVertical: true,
                validators: [{ type: 'required', errorMessage: '' }]
            }
        },
        description: format(
            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AmazonS3_DisplayText),
            stringResources.ModelName_AmazonAccessKey
        )
    }
];

export const amazonS3Metadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {

    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.amazonS3.linkedServiceType,
        displayName: stringResources.AmazonS3_DisplayText,
        supportedInTrident: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'AmazonS3' as ResourceKind
        },
        supportedAuthKindsInTrident: [ConnectionAuthKind.Basic],
        linkedServiceKind: 'file',
        icon: {
            className: 'svg-i-100x100-AmazonS3'
        },
        isFirstClass: true,
        supportDataFlowSource: true,
        supportDataFlowSink: true,
        supportOPDG: GlobalVariables.enableOPDG,
        dataflowSource: [avro, cdm, delimited, delta, excel, json, orc, parquet, xml, iceberg],
        dataflowSink: [avro, cdm, delimited, delta, json, orc, parquet, iceberg],
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        ...(GlobalVariables.copyJobGenericCdc ? {
            cdc: {
                supportCDC: true,
                supportedCDCDestinations: cdcDestination,
                supportGenericCdc: true
            }
        } : {}),
        supportGetMetadataActivity: true,
        supportIncrementalBinaryCopy: true,
        supportDatasetV2: true,
        notSupportCopySink: !__IS_FABRIC_DPE__,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        locationPattern: {
            bucketName: '([^/]*)/?[^]*',
            folderPath: '[^/]*/([^]*)/[^/]*',
            fileName: '[^]*/([^/]*)'
        },
        containerLabel: stringResources.InputBucketPlaceHolder,
        containerLabelPlural: stringResources.InputBucketsPlaceHolder,
        locationPropertiesRequireParameters: ['bucketName', 'folderPath', 'fileName'],
        mergeReadSettingsInTrident: true,
        getMetadataActivityReadProperties: commonFileTypeStoreGetMetadataActivityReadSettings,
        supportFirstClassParameterExperience: true,
        deleteActivityReadProperties: commonFileTypeStoreDeleteActivityReadSettings,
        supportTestConnectionToFilePath: true,
        storeReadSettingControls: getCommonFileTypeStoreReadSettingControls(true),
        datasetTypeToStoreReadSettingControls: {
            [binaryMetadata.type]: getCommonBinaryStoreReadSettingControls(true),
            [deltaMetadata.type]: getCommonDeltaStoreReadSettingControls()
        },
        storeWriteSettingControls: [...commonFileTypeStoreWriteSettingControls],
        datasetTypeToStoreWriteSettingControls: {
            [binaryMetadata.type]: [...commonBinaryStoreWriteSettingControls],
            [deltaMetadata.type]: [...commonDeltaStoreWriteSettingControls]
        },
        category: ['File'],
        controls: [
            {
                type: 'select-switch',
                bind: 'authType',
                label: stringResources.ModelName_AuthenticationType,
                options: [
                    {
                        label: stringResources.ModelName_AzureBatchAccessKey, // will rename this label later
                        value: s3AuthType.accessKey
                    },
                    {
                        label: stringResources.AmazonS3_AuthTypeTempToken,
                        value: s3AuthType.temporarySecurity
                    }
                ],
                isLabelVertical: true,
                defaultValue: s3AuthType.accessKey,
                cases: [
                    {
                        caseValue: s3AuthType.accessKey,
                        children: commonAccessProperties
                    },
                    {
                        caseValue: s3AuthType.temporarySecurity,
                        children: commonAccessProperties.concat([
                            {
                                type: 'dynamic-akv-switcher',
                                enableAKVParameterization: true,
                                bind: 'sessionToken',
                                label: stringResources.ModelName_SessionToken,
                                child: {
                                    type: 'parameterizable',
                                    attribute: 'sessionToken',
                                    disableAddParameter: true,
                                    child: {
                                        type: 'password',
                                        label: stringResources.ModelName_SessionToken,
                                        required: true,
                                        isLabelVertical: true,
                                        validators: [{ type: 'required', errorMessage: '' }]
                                    }
                                },
                                description: format(
                                    stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                    format(
                                        stringResources.ModelName_LinkedServiceTemplate,
                                        stringResources.AmazonS3_DisplayText
                                    ),
                                    stringResources.ModelName_SessionToken
                                )
                            },
                            {
                                type: 'hint',
                                description: stringResources.S3TempCredential_ExpirationMessage,
                                link: 'https://go.microsoft.com/fwlink/?linkid=2146423'
                            }
                        ])
                    }
                ]
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox',
                    bind: 'serviceUrl',
                    label: stringResources.ModelName_ServiceUrl,
                    escapeDescription: false,
                    isLabelVertical: true,
                    description: stringResources.AmazonS3_ServiceUrl_Description,
                    warningMessageCalculator: (value: string) =>
                        stringIsNullOrEmpty(value) || value === 'https://s3.amazonaws.com'
                            ? ''
                            : stringResources.infoSentViaUrlWarning
                },
                warningMessageCalculator: (value: string) =>
                    stringIsNullOrEmpty(value) ? '' : stringResources.infoSentViaUrlWarning
            }
        ],
        copySinkTypes: [],
        copySourceTypes: [Metamodel.modelFor(FileSystemCopySource).name]
    };

    return metadata;
};
